import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import { useCheckoutState } from '../../utils/checkoutState';
import { config } from '../../utils/config';
import { reportErrorToSentry } from '../../utils/initializeSentry';
import useCss from '../../utils/useCss';
import useCustomTranslations from '../../utils/useCustomTranslations';
import useScript from '../../utils/useScript';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    AdyenCheckout: any;
  }
}

type AdyenCreditcardSpecification = {
  paymentMethod: Record<string, unknown>;
  browserInfo: Record<string, unknown>;
};

// https://docs.adyen.com/online-payments/api-only
// https://docs.adyen.com/payment-methods/cards/custom-card-integration

const AdyenCreditcardPayment = () => {
  const adyenSdkHost = config.isProduction ? 'checkoutshopper-live.adyen.com' : 'checkoutshopper-test.adyen.com';
  const scriptLoadingPromise = useScript(
    `https://${adyenSdkHost}/checkoutshopper/sdk/5.0.0/adyen.js`,
    'sha384-kcKKvS6qZbXycrUw31OJ2/2Hz8A8FTsV9anjvpyQc/IWR1SmkFUw7w7F/t5S3qtA'
  );
  useCss(`https://${adyenSdkHost}/checkoutshopper/sdk/5.0.0/adyen.css`);
  const adyenComponentRef = useRef<any>();
  const { checkoutState } = useCheckoutState();
  const { values, setFieldValue } = useFormikContext<{
    paymentMethod: string;
    adyenCreditcard: AdyenCreditcardSpecification;
  }>();
  const selectedPaymentMethod = checkoutState.orderState.payment?.selected?.[0];
  const requiresTranslation = selectedPaymentMethod?.configuration?.public?.requiresTranslation;
  const initializationData = selectedPaymentMethod?.configuration?.initializationData;
  const previousInitializationData = useRef();
  const formattedLocale = config.locale.replace('_', '-');
  const { adyenCreditCard } = useCustomTranslations();

  useEffect(() => {
    if (
      !initializationData ||
      JSON.stringify(initializationData) === JSON.stringify(previousInitializationData.current) ||
      selectedPaymentMethod?.paymentOptionKey !== values.paymentMethod
    ) {
      return;
    }

    previousInitializationData.current = initializationData;

    (async () => {
      await scriptLoadingPromise.current;

      const configuration = {
        environment: config.isProduction ? 'live' : 'test',
        clientKey: initializationData.clientKey,
        session: {
          id: initializationData.sessionId,
          sessionData: initializationData.sessionData,
        },
        onChange: ({
          isValid,
          data: { paymentMethod, browserInfo },
        }: {
          isValid: boolean;
          data: AdyenCreditcardSpecification;
        }) => {
          setFieldValue('adyenCreditcard', { paymentMethod, browserInfo, isValid });
        },
        onError: (error: unknown) => {
          console.error(error);
          reportErrorToSentry(error);
        },
        locale: formattedLocale,
        translations: requiresTranslation && {
          [formattedLocale]: adyenCreditCard,
        },
      };
      const checkout = await window.AdyenCheckout(configuration);
      const adyenComponent = checkout.create('card');
      adyenComponent.mount('#adyen-payment-container');
      adyenComponentRef.current = adyenComponent;
    })();
  }, [initializationData]);

  useEffect(
    () => () => {
      const adyenComponent = adyenComponentRef.current;
      if (adyenComponent) {
        adyenComponent.unmount();
      }
    },
    []
  );

  return (
    <div className="option__form">
      {IS_WEB_COMPONENT ? <slot name="adyen-payment-container" /> : <div id="adyen-payment-container" />}
    </div>
  );
};

export default AdyenCreditcardPayment;
